import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useState,
} from "react";
import { authAPI } from "../services/api";
import toast from "react-hot-toast";
import PasswordChangeModal from "../components/PasswordChangeModal";

const AuthContext = createContext();

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN_START":
      return {
        ...state,
        loading: true,
        error: null,
      };
    case "LOGIN_SUCCESS":
      // Store user data in localStorage
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      return {
        ...state,
        loading: false,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        error: null,
      };
    case "LOGIN_FAILURE":
      return {
        ...state,
        loading: false,
        user: null,
        token: null,
        isAuthenticated: false,
        error: action.payload,
      };
    case "LOGOUT":
      // Clear user data from localStorage
      localStorage.removeItem("user");
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };
    case "UPDATE_USER":
      const updatedUser = { ...state.user, ...action.payload };
      // Update user data in localStorage
      localStorage.setItem("user", JSON.stringify(updatedUser));
      return {
        ...state,
        user: updatedUser,
      };
    case "SET_LOADING":
      return {
        ...state,
        loading: action.payload,
      };
    default:
      return state;
  }
};

// Initial state
const initialState = (() => {
  const storedUser = (() => {
    try {
      const userData = localStorage.getItem("user");
      return userData && userData !== "undefined" && userData !== "null"
        ? JSON.parse(userData)
        : null;
    } catch (error) {
      console.error("Error parsing stored user data:", error);
      return null;
    }
  })();

  const storedToken = (() => {
    // Check both localStorage and sessionStorage for token
    const rememberMe = localStorage.getItem("rememberMe") === "true";
    const tokenExpiry = localStorage.getItem("tokenExpiry");

    if (rememberMe) {
      // Check if token has expired
      if (tokenExpiry && Date.now() > parseInt(tokenExpiry)) {
        // Token expired, clear storage
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("tokenExpiry");
        return null;
      }
      return localStorage.getItem("token");
    } else {
      return sessionStorage.getItem("token") || localStorage.getItem("token");
    }
  })();

  return {
    user: storedUser,
    token: storedToken,
    isAuthenticated: !!(storedUser && storedToken), // Set to true if both exist
    loading: true,
    error: null,
  };
})();

// Helper function to clear corrupted localStorage data
const clearCorruptedStorage = () => {
  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser === "undefined" || storedUser === "null") {
      localStorage.removeItem("user");
      console.log("Cleared corrupted user data from localStorage");
    }
  } catch (error) {
    console.error("Error checking localStorage:", error);
    localStorage.removeItem("user");
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const hasShownWelcomeToast = useRef(false);
  const isGoogleOAuthLogin = useRef(false);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);

  // Clear any corrupted localStorage data on mount
  useEffect(() => {
    clearCorruptedStorage();
  }, []);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      // Check for token in both localStorage and sessionStorage
      const rememberMe = localStorage.getItem("rememberMe") === "true";
      const tokenExpiry = localStorage.getItem("tokenExpiry");

      let token = null;
      if (rememberMe) {
        // Check if token has expired
        if (tokenExpiry && Date.now() > parseInt(tokenExpiry)) {
          // Token expired, clear storage
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("rememberMe");
          localStorage.removeItem("tokenExpiry");
        } else {
          token = localStorage.getItem("token");
        }
      } else {
        token =
          sessionStorage.getItem("token") || localStorage.getItem("token");
      }

      let storedUser = null;
      try {
        const storedUserData = localStorage.getItem("user");
        storedUser =
          storedUserData &&
          storedUserData !== "undefined" &&
          storedUserData !== "null"
            ? JSON.parse(storedUserData)
            : null;
      } catch (error) {
        console.error("Error parsing stored user data in checkAuth:", error);
        storedUser = null;
      }

      console.log(
        "checkAuth called with token:",
        token ? `${token.substring(0, 20)}...` : "null"
      );
      console.log("Remember me:", rememberMe);
      console.log("Stored user data:", storedUser);
      console.log("isGoogleOAuthLogin flag:", isGoogleOAuthLogin.current);

      if (token) {
        // If we have stored user data and it's not a Google OAuth login in progress, use it immediately
        if (storedUser && !isGoogleOAuthLogin.current) {
          console.log("Using stored user data for immediate display");
          dispatch({
            type: "LOGIN_SUCCESS",
            payload: {
              user: storedUser,
              token,
            },
          });
        }

        try {
          const response = await authAPI.getCurrentUser();
          console.log("Token verification successful:", response.data);
          console.log("User data from /auth/me:", response.data.data?.user);

          // Only update user data if it's not a Google OAuth login in progress
          if (!isGoogleOAuthLogin.current && response.data.data?.user) {
            dispatch({
              type: "LOGIN_SUCCESS",
              payload: {
                user: response.data.data.user,
                token,
              },
            });
          } else {
            console.log(
              "Skipping user data update - Google OAuth login in progress or no user data"
            );
            dispatch({ type: "SET_LOADING", payload: false });
          }
        } catch (error) {
          console.error("Token verification failed:", error);
          // Clear all storage on token verification failure
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("rememberMe");
          localStorage.removeItem("tokenExpiry");
          sessionStorage.removeItem("token");
          dispatch({ type: "LOGOUT" });
        }
      } else {
        console.log("No token found, setting loading to false");
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    // Only run checkAuth on initial load, not when user changes
    checkAuth();
  }, []); // Empty dependency array - only run once on mount

  // Login function
  const login = async (credentials, rememberMe = false) => {
    console.log("=== AUTH CONTEXT LOGIN ===");
    console.log("Credentials email:", credentials.email);
    console.log("Remember me:", rememberMe);

    dispatch({ type: "LOGIN_START" });
    try {
      const response = await authAPI.login(credentials, rememberMe);
      console.log("API response:", response.data);

      const { user, token, isFirstLogin } = response.data.data;
      console.log("User from API:", user);
      console.log(
        "Token from API:",
        token ? `${token.substring(0, 20)}...` : "null"
      );
      console.log("isFirstLogin:", isFirstLogin);

      // Store token with appropriate expiration
      if (rememberMe) {
        // Store in localStorage for 30 days
        localStorage.setItem("token", token);
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem(
          "tokenExpiry",
          Date.now() + 30 * 24 * 60 * 60 * 1000
        ); // 30 days
        console.log("Token stored in localStorage (30 days)");
      } else {
        // Store in sessionStorage for session only
        sessionStorage.setItem("token", token);
        localStorage.setItem("rememberMe", "false");
        localStorage.removeItem("tokenExpiry");
        console.log("Token stored in sessionStorage (session only)");
      }

      // Store user in localStorage
      localStorage.setItem("user", JSON.stringify(user));
      console.log("User stored in localStorage");

      console.log("Dispatching LOGIN_SUCCESS...");
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: { user, token },
      });
      console.log("LOGIN_SUCCESS dispatched");

      // Handle first login (temporary password)
      if (isFirstLogin) {
        toast.success(
          `Welcome, ${user.firstName}! Please change your password.`,
          {
            duration: 8000,
          }
        );
        setShowPasswordChangeModal(true);
      } else {
        toast.success(`Welcome back, ${user.firstName}!`);
      }

      console.log("Returning success with user:", user);
      return { success: true, isFirstLogin, user };
    } catch (error) {
      console.error("Login error:", error);
      console.error("Error response:", error.response?.data);
      const errorMessage = error.response?.data?.message || "Login failed";
      dispatch({
        type: "LOGIN_FAILURE",
        payload: errorMessage,
      });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Register function
  const register = async (userData, rememberMe = false) => {
    dispatch({ type: "LOGIN_START" });
    try {
      const response = await authAPI.register(userData, rememberMe);
      const { user, token } = response.data.data;

      // Store token with appropriate expiration
      if (rememberMe) {
        // Store in localStorage for 30 days
        localStorage.setItem("token", token);
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem(
          "tokenExpiry",
          Date.now() + 30 * 24 * 60 * 60 * 1000
        ); // 30 days
      } else {
        // Store in sessionStorage for session only
        sessionStorage.setItem("token", token);
        localStorage.setItem("rememberMe", "false");
        localStorage.removeItem("tokenExpiry");
      }

      dispatch({
        type: "LOGIN_SUCCESS",
        payload: { user, token },
      });

      toast.success(`Welcome to NourishGY, ${user.firstName}!`);
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Registration failed";
      dispatch({
        type: "LOGIN_FAILURE",
        payload: errorMessage,
      });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = () => {
    // Clear all storage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("rememberMe");
    localStorage.removeItem("tokenExpiry");
    sessionStorage.removeItem("token");
    dispatch({ type: "LOGOUT" });
    hasShownWelcomeToast.current = false; // Reset toast flag
    isGoogleOAuthLogin.current = false; // Reset Google OAuth flag
    toast.success("Logged out successfully");
  };

  // Update user profile
  const updateUser = (userData) => {
    dispatch({
      type: "UPDATE_USER",
      payload: userData,
    });
  };

  // Change password
  const changePassword = async (passwordData) => {
    try {
      await authAPI.changePassword(passwordData);
      toast.success("Password changed successfully");
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to change password";
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Login with Google OAuth token
  const loginWithToken = async (token) => {
    dispatch({ type: "LOGIN_START" });
    try {
      // Verify token with backend
      const response = await authAPI.verifyToken(token);
      const { user } = response.data.data;

      localStorage.setItem("token", token);
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: { user, token },
      });

      toast.success(`Welcome, ${user.firstName}!`);
      return { success: true, user };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Token verification failed";
      dispatch({
        type: "LOGIN_FAILURE",
        payload: errorMessage,
      });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Login with Google OAuth token and user data
  const loginWithGoogleData = async (token, userData) => {
    console.log("loginWithGoogleData called with:", {
      token: token ? `${token.substring(0, 20)}...` : "null",
      userData,
    });
    console.log("User data keys:", userData ? Object.keys(userData) : "null");
    console.log("User data firstName:", userData?.firstName);
    console.log("User data first_name:", userData?.first_name);

    // Set flag to prevent checkAuth from overriding
    isGoogleOAuthLogin.current = true;

    dispatch({ type: "LOGIN_START" });
    try {
      localStorage.setItem("token", token);
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: { user: userData, token },
      });

      console.log("User data set in context:", userData);

      // Reset the Google OAuth flag after a delay to allow page reloads to work
      setTimeout(() => {
        isGoogleOAuthLogin.current = false;
        console.log(
          "Google OAuth flag reset - page reloads will now fetch user data"
        );
      }, 3000); // 3 second delay

      // Only show welcome toast once
      if (!hasShownWelcomeToast.current) {
        toast.success(`Welcome, ${userData.firstName}!`);
        hasShownWelcomeToast.current = true;
      }
      return { success: true, user: userData };
    } catch (error) {
      console.error("loginWithGoogleData error:", error);
      const errorMessage = "Google authentication failed";
      dispatch({
        type: "LOGIN_FAILURE",
        payload: errorMessage,
      });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Initiate Google OAuth login
  const loginWithGoogle = () => {
    const backendUrl =
      process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";
    window.location.href = `${backendUrl}/auth/google`;
  };

  // Password change modal handlers
  const handlePasswordChangeSuccess = () => {
    setShowPasswordChangeModal(false);
    toast.success(
      "Password changed successfully! You can now access your dashboard."
    );
  };

  const handlePasswordChangeClose = () => {
    // Don't allow closing the modal on first login - they must change password
    if (state.user?.role === "vendor") {
      toast.error("You must change your password before continuing.");
      return;
    }
    setShowPasswordChangeModal(false);
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    changePassword,
    loginWithToken,
    loginWithGoogleData,
    loginWithGoogle,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <PasswordChangeModal
        isOpen={showPasswordChangeModal}
        onClose={handlePasswordChangeClose}
        onSuccess={handlePasswordChangeSuccess}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
